/* eslint-disable react-hooks/exhaustive-deps */
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { AppointmentRequest } from '@dataTypes/appointment.type'
import { IPatient, IChatParticipant } from '@dataTypes/chat.type'
import { IContact, IContactGroup } from '@dataTypes/contact.type'
import {
  addBlockedUser,
  deleteContact,
  fetchAllBlockedUsers,
  fetchAllCallLogs,
  fetchAllContacts,
  fetchAllConversations,
  fetchAllFax,
  fetchAllCoversheets,
  fetchAllFaxTags,
  fetchAllFaxFolders,
  fetchEmailMessages,
  updateBlockedUser,
  updateConversation,
  updateSelectedMessage,
} from '@store/actions'
import { addAppointment } from '@store/actions/appointment.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks'
import {
  ModalState,
  SET_ACTIVE_TAB,
  SET_CONTACT,
  SET_PATIENT,
  SET_SHOW_MODAL,
} from '@store/reducers/messageCenter.reducer'
import { createNewTwilioDevice } from '@utils/device.twilio'
import { fetchPhoneManagementData } from '@store/actions/settings-communication.action'
import moment from 'moment'
import {
  Dispatch,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from 'react'
import { upsertEmailDraftFromComposer } from '@store/reducers/email.reducer'
import { upsertEmailDraft } from '@store/actions/email.action'
import { isEmailBackendEnabled } from '@services/api/email.api'
import { EmailComposerDraft } from '@dataTypes/email.type'
import { useLocation } from 'react-router-dom'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'

const DEFAULT_EMAIL_COMPOSER_DRAFT: EmailComposerDraft = {
  recipientEmail: '',
  to: undefined,
  cc: undefined,
  bcc: undefined,
  subject: '',
  body: '',
  modalTitle: 'Compose email',
}

type MessageHooks = {
  activeTab: string
  contact: IContact | null
  handleButtonClick: (tab: string) => void
  handleUndo: () => void
  tabLayerActions: ReactNode | null
  tabLayerContextValue: {
    setTabLayerActions: Dispatch<React.SetStateAction<ReactNode | null>>
  }
  onAddContactGroupModalHide: () => void
  onAddNoteModalHide: () => void
  onAppointmentModalHide: () => void
  onAppointmentSave: (apptDetails: Partial<AppointmentRequest>) => void
  onBlockModalHide: () => void
  emailComposerShow: boolean
  emailComposerDraft: EmailComposerDraft
  onEmailComposerHide: () => void
  onEmailComposerAutoSaveAndHide: () => void
  onEmailComposerDraftChange: (draft: Partial<EmailComposerDraft>) => void
  openEmailComposer: (draft?: Partial<EmailComposerDraft>) => void
  onConfirmBlockUser: () => void
  onConfirmDeleteContact: () => void
  onDeleteModalHide: () => void
  onManageGroupModalHide: () => void
  participant: IChatParticipant | null
  patient: IPatient | null
  selectedContactGroup: IContactGroup | null
  showModal: ModalState
  toasterText: string | null
  isTabSkeleton: boolean
}

const MESSAGE_CENTER_LOADING_CONTAINER = '.message-container-page'
const MESSAGE_CENTER_TAB_SKELETON_MS = 500

const showMessageCenterTabLoading = (
  showLoading: ReturnType<typeof useGlobalLoadingActions>['showLoading'],
): void => {
  showLoading({
    container: MESSAGE_CENTER_LOADING_CONTAINER,
    style: FIRST_LOAD_OVERLAY_STYLE,
  })
}

const stripHtmlAndNormalize = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const normalizeRecipients = (list?: string[]): string[] =>
  [...(list ?? [])]
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
    .sort()

const normalizeDraftForCompare = (
  draft: EmailComposerDraft,
): {
  recipientEmail: string
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  bodyText: string
  attachments: string[]
} => ({
  recipientEmail: draft.recipientEmail.trim().toLowerCase(),
  to: normalizeRecipients(draft.to),
  cc: normalizeRecipients(draft.cc),
  bcc: normalizeRecipients(draft.bcc),
  subject: draft.subject.trim(),
  bodyText: stripHtmlAndNormalize(draft.body),
  attachments: [...(draft.attachments ?? [])].map((att) => att.id).sort(),
})

const hasAnyEmailDraftContent = (draft: EmailComposerDraft): boolean =>
  (draft.to?.join('') ?? '').trim().length > 0 ||
  (draft.cc?.join('') ?? '').trim().length > 0 ||
  (draft.bcc?.join('') ?? '').trim().length > 0 ||
  draft.recipientEmail.trim().length > 0 ||
  draft.subject.trim().length > 0 ||
  stripHtmlAndNormalize(draft.body).length > 0

const useMessageCenter = (): MessageHooks => {
  const { showToast } = useNotificationToast()
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const activeTab = useAppSelector((state) => state.messageCenter.activeTab)
  const device = useAppSelector((state) => state.calls.device)
  const conversations = useAppSelector((state) => state.chat.data)
  const calls = useAppSelector((state) => state.calls.data)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)

  const [tabLayerActions, setTabLayerActions] = useState<ReactNode | null>(null)
  const [isTabSkeleton, setIsTabSkeleton] = useState(false)
  const [isPageReady, setIsPageReady] = useState(false)
  const isPageReadyRef = useRef(false)

  const tabLayerContextValue = useMemo(
    () => ({
      setTabLayerActions,
    }),
    [],
  )
  const prevClinicIdRef = useRef<string | null>(null)
  const loadedTabsByClinicRef = useRef<{
    clinicId: string | null
    tabs: Set<string>
  }>({ clinicId: null, tabs: new Set() })

  const showModal = useAppSelector((state) => state.messageCenter.show)
  const toasterText = useAppSelector((state) => state.messageCenter.toaster)
  const participant = useAppSelector((state) => state.messageCenter.participant)
  const contact = useAppSelector((state) => state.messageCenter.contact)
  const selectedContactGroup = useAppSelector(
    (state) => state.messageCenter.group,
  )
  const conversationId = useAppSelector(
    (state) => state.messageCenter.lastActiveConversation,
  )
  const patient = useAppSelector((state) => state.messageCenter.patient)
  const lastBlockedUser = useAppSelector(
    (state) => state.messageCenter.lastBlockedUser,
  )
  const [emailComposerShow, setEmailComposerShow] = useState(false)
  const [emailComposerDraft, setEmailComposerDraft] =
    useState<EmailComposerDraft>(DEFAULT_EMAIL_COMPOSER_DRAFT)
  const location = useLocation()
  const lastComposerPathRef = useRef(location.pathname)
  const didAutoSaveOnLeaveRef = useRef(false)
  const emailComposerShowRef = useRef(emailComposerShow)
  const emailComposerDraftRef = useRef(emailComposerDraft)
  const initialEmailComposerDraftRef = useRef<EmailComposerDraft>(
    DEFAULT_EMAIL_COMPOSER_DRAFT,
  )
  const handleButtonClick = (tab: string): void => {
    if (tab === activeTab) return
    if (isPageReadyRef.current) {
      setIsTabSkeleton(true)
    }
    dispatch(SET_ACTIVE_TAB(tab))
  }

  const onAddContactGroupModalHide = (): void => {
    dispatch(SET_SHOW_MODAL({ group: false }))
    dispatch(SET_CONTACT(null))
  }

  const onAddNoteModalHide = (): void => {
    dispatch(SET_SHOW_MODAL({ addNote: false }))
  }

  const onBlockModalHide = (): void => {
    dispatch(SET_SHOW_MODAL({ block: false }))
    dispatch(SET_CONTACT(null))
  }

  const onDeleteModalHide = (): void => {
    dispatch(SET_SHOW_MODAL({ delete: false }))
    dispatch(SET_CONTACT(null))
  }

  const onManageGroupModalHide = (): void => {
    dispatch(SET_SHOW_MODAL({ manageGroup: false }))
  }

  const onAppointmentModalHide = (): void => {
    dispatch(SET_SHOW_MODAL({ appointment: false }))
  }

  const onAppointmentSave = (
    apptDetails: Partial<AppointmentRequest>,
  ): void => {
    dispatch(
      addAppointment({
        appointmentData: apptDetails,
        date: moment().format('MMMM D, YYYY'),
        activeTab: 'day',
      }),
    )
  }

  const onConfirmBlockUser = async (): Promise<void> => {
    if (participant && participant.contact) {
      const clinicId = selectedClinic?.clinic_id ?? null
      const _contact = participant.contact
      const phoneNumber =
        participant.phone_number ||
        _contact.cellphone ||
        _contact.home_phone ||
        _contact.work_phone ||
        null

      if (!clinicId) {
        showToast(
          createFailedToast(
            'Error',
            'No clinic selected. Cannot block this chat.',
          ),
        )
        dispatch(updateSelectedMessage())
        return
      }

      if (!phoneNumber) {
        showToast(
          createFailedToast(
            'Error',
            'This contact has no phone number to block.',
          ),
        )
        dispatch(updateSelectedMessage())
        return
      }

      try {
        await dispatch(
          addBlockedUser({
            clinic_id: clinicId,
            contact_id: _contact.contact_id ?? null,
            id: participant.chat_participant_id ?? null,
            phone_number: phoneNumber,
            reason: 'Blocked from chat',
            blocked_features: ['sms_chat'],
            created_by: '',
            is_hidden: false,
          }),
        ).unwrap()
        showToast(createSuccessToast('Info', 'Chat blocked.'))
      } catch (err) {
        const message =
          typeof err === 'string'
            ? err
            : err && typeof err === 'object' && 'message' in err
              ? String((err as { message: unknown }).message)
              : 'Blocking chat failed. Please try again.'
        showToast(createFailedToast('Error', message))
      }
    }

    dispatch(updateSelectedMessage())
  }

  const onConfirmDeleteContact = async (): Promise<void> => {
    if (!contact?.contact_id) return
    try {
      showLoading({ container: MESSAGE_CENTER_LOADING_CONTAINER })
      await dispatch(deleteContact(contact.contact_id)).unwrap()
      showToast(createSuccessToast('Success', 'Contact deleted successfully.'))
      onDeleteModalHide()
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to delete contact. Please try again.'
      showToast(createFailedToast('Error', message))
    } finally {
      hideLoading(MESSAGE_CENTER_LOADING_CONTAINER)
    }
  }

  const openEmailComposer = useCallback(
    (draft?: Partial<EmailComposerDraft>): void => {
      didAutoSaveOnLeaveRef.current = false
      const nextDraft = {
        ...DEFAULT_EMAIL_COMPOSER_DRAFT,
        ...draft,
      }
      initialEmailComposerDraftRef.current = nextDraft
      setEmailComposerDraft(nextDraft)
      setEmailComposerShow(true)
    },
    [],
  )

  const onEmailComposerHide = useCallback((): void => {
    setEmailComposerShow(false)
  }, [])

  const onEmailComposerDraftChange = useCallback(
    (draft: Partial<EmailComposerDraft>): void => {
      didAutoSaveOnLeaveRef.current = false
      setEmailComposerDraft((prev) => ({ ...prev, ...draft }))
    },
    [],
  )

  const autoSaveEmailDraftIfNeeded = useCallback((): void => {
    const currentComposerShow = emailComposerShowRef.current
    const currentDraft = emailComposerDraftRef.current

    if (!currentComposerShow) return
    if (!hasAnyEmailDraftContent(currentDraft)) return
    const initialDraft = initialEmailComposerDraftRef.current
    const hasMeaningfulChange =
      JSON.stringify(normalizeDraftForCompare(currentDraft)) !==
      JSON.stringify(normalizeDraftForCompare(initialDraft))
    if (!hasMeaningfulChange) return

    const draftId = currentDraft.draftMessageId ?? `draft-${Date.now()}`
    const normalizedDraft: EmailComposerDraft = {
      ...currentDraft,
      draftMessageId: draftId,
    }
    emailComposerDraftRef.current = normalizedDraft
    setEmailComposerDraft(normalizedDraft)
    dispatch(
      upsertEmailDraftFromComposer({ id: draftId, draft: normalizedDraft }),
    )
    showToast(
      createSuccessToast('Email Draft', 'The email draft has been saved.'),
    )
    didAutoSaveOnLeaveRef.current = true

    if (isEmailBackendEnabled()) {
      void dispatch(upsertEmailDraft(normalizedDraft))
        .unwrap()
        .then((saved) => {
          const savedDraft: EmailComposerDraft = {
            ...normalizedDraft,
            draftMessageId: saved.id,
          }
          emailComposerDraftRef.current = savedDraft
          setEmailComposerDraft(savedDraft)
          dispatch(
            upsertEmailDraftFromComposer({ id: saved.id, draft: savedDraft }),
          )
        })
        .catch(() => undefined)
    }
  }, [dispatch, showToast])

  const onEmailComposerAutoSaveAndHide = useCallback((): void => {
    autoSaveEmailDraftIfNeeded()
    setEmailComposerShow(false)
  }, [autoSaveEmailDraftIfNeeded])

  useEffect(() => {
    emailComposerShowRef.current = emailComposerShow
  }, [emailComposerShow])

  useEffect(() => {
    emailComposerDraftRef.current = emailComposerDraft
  }, [emailComposerDraft])

  useEffect(() => {
    const previousPath = lastComposerPathRef.current
    const currentPath = location.pathname
    const leftMessagesPage =
      previousPath.startsWith('/message') && !currentPath.startsWith('/message')
    if (leftMessagesPage) {
      autoSaveEmailDraftIfNeeded()
    }
    lastComposerPathRef.current = currentPath
  }, [location.pathname, autoSaveEmailDraftIfNeeded])

  useEffect(() => {
    return () => {
      if (!didAutoSaveOnLeaveRef.current) {
        autoSaveEmailDraftIfNeeded()
      }
    }
  }, [autoSaveEmailDraftIfNeeded])

  const handleUndo = (): void => {
    if (toasterText?.includes('unpinned')) {
      dispatch(
        updateConversation({
          id: conversationId,
          data: { is_pinned: true },
        }),
      )
    } else if (toasterText?.includes('pinned')) {
      dispatch(
        updateConversation({
          id: conversationId,
          data: { is_pinned: false },
        }),
      )
    } else if (toasterText?.includes('blocked')) {
      dispatch(
        updateBlockedUser({
          blocked_user_id: lastBlockedUser,
          data: { is_hidden: true },
        }),
      )
    } else if (toasterText?.includes('deleted')) {
      dispatch(
        updateConversation({
          id: conversationId,
          data: { is_hidden: false },
        }),
      )
    }
  }

  useEffect(() => {}, [calls, conversations, device, dispatch])

  useEffect(() => {
    dispatch(fetchAllBlockedUsers())

    return () => {
      dispatch(SET_PATIENT(null))
      dispatch(SET_CONTACT(null))
    }
  }, [])

  useEffect(() => {
    if (!selectedClinic) {
      return
    }

    const currentClinicId = selectedClinic.clinic_id
    const prevClinicId = prevClinicIdRef.current

    if (prevClinicId === null) {
      prevClinicIdRef.current = currentClinicId
      return
    }

    if (prevClinicId === currentClinicId) return

    prevClinicIdRef.current = currentClinicId

    void dispatch(updateSelectedMessage())
    if (!device && selectedClinic.clinic_id) {
      void dispatch(fetchPhoneManagementData(selectedClinic.clinic_id))
      createNewTwilioDevice(dispatch)
    }
  }, [selectedClinic, dispatch, device])

  useLayoutEffect(() => {
    showMessageCenterTabLoading(showLoading)
  }, [showLoading])

  useLayoutEffect(() => {
    const container = document.querySelector(MESSAGE_CENTER_LOADING_CONTAINER)
    if (!(container instanceof HTMLElement)) return
    container.classList.toggle('is-ready', isPageReady)
  }, [isPageReady])

  useEffect(() => {
    let cancelled = false
    const startedAt = Date.now()
    const clinicId = selectedClinic?.clinic_id ?? null
    const clinicChanged =
      clinicId !== null && loadedTabsByClinicRef.current.clinicId !== clinicId
    const useOverlay = !isPageReadyRef.current || clinicChanged

    if (useOverlay) {
      setIsPageReady(false)
      setIsTabSkeleton(false)
      showMessageCenterTabLoading(showLoading)
    } else {
      hideLoading(MESSAGE_CENTER_LOADING_CONTAINER)
      setIsTabSkeleton(true)
    }

    const finish = async (): Promise<void> => {
      await waitAtLeast(
        startedAt,
        useOverlay ? FIRST_LOAD_OVERLAY_MS : MESSAGE_CENTER_TAB_SKELETON_MS,
      )
      if (cancelled) return
      if (useOverlay) {
        isPageReadyRef.current = true
        setIsPageReady(true)
        hideLoading(MESSAGE_CENTER_LOADING_CONTAINER)
      }
      setIsTabSkeleton(false)
    }

    if (!clinicId) {
      void finish()
      return () => {
        cancelled = true
      }
    }

    if (clinicChanged) {
      loadedTabsByClinicRef.current = { clinicId, tabs: new Set() }
    }

    const loadActiveTab = async (): Promise<void> => {
      if (!loadedTabsByClinicRef.current.tabs.has(activeTab)) {
        try {
          switch (activeTab) {
            case 'contact':
              await dispatch(fetchAllContacts()).unwrap()
              break
            case 'chat':
              await dispatch(fetchAllConversations()).unwrap()
              break
            case 'calls':
              await dispatch(fetchAllCallLogs()).unwrap()
              break
            case 'fax':
              await Promise.all([
                dispatch(fetchAllFax()).unwrap(),
                dispatch(fetchAllFaxFolders()).unwrap(),
                dispatch(fetchAllFaxTags()).unwrap(),
                dispatch(fetchAllCoversheets()).unwrap(),
              ])
              break
            case 'email':
              await dispatch(fetchEmailMessages()).unwrap()
              break
            default:
              break
          }
          if (!cancelled) {
            loadedTabsByClinicRef.current.tabs.add(activeTab)
          }
        } catch {
          // Tab data is best-effort; empty/error UI handles the rest.
        }
      }
      await finish()
    }

    void loadActiveTab()

    return () => {
      cancelled = true
    }
  }, [activeTab, selectedClinic?.clinic_id, dispatch, showLoading, hideLoading])

  useEffect(() => {
    return () => {
      hideLoading(MESSAGE_CENTER_LOADING_CONTAINER)
    }
  }, [hideLoading])

  return {
    activeTab,
    contact,
    handleButtonClick,
    handleUndo,
    tabLayerActions,
    tabLayerContextValue,
    onAddContactGroupModalHide,
    onAddNoteModalHide,
    onAppointmentModalHide,
    onAppointmentSave,
    onBlockModalHide,
    onConfirmBlockUser,
    onConfirmDeleteContact,
    onDeleteModalHide,
    onManageGroupModalHide,
    participant,
    patient,
    selectedContactGroup,
    showModal,
    emailComposerShow,
    emailComposerDraft,
    onEmailComposerHide,
    onEmailComposerAutoSaveAndHide,
    onEmailComposerDraftChange,
    openEmailComposer,
    toasterText,
    isTabSkeleton,
  }
}

export default useMessageCenter
